import React, { useState } from 'react';
import {
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Container,
  useScrollTrigger,
  Slide,
  Fab,
  Grid,
  Card,
  CardContent,
  Modal,
  AppBar,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Close as CloseIcon,
  LocationOn as LocationOnIcon,
  Email as EmailIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import './App.css';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';

// Tema personalizado
const theme = createTheme({
  palette: {
    primary: { main: '#ff6b35' },
    secondary: { main: '#1450f0' },
    success: { main: '#25d366' },
    background: { default: '#f0f2f5', paper: '#ffffff' },
    text: { primary: '#121212', secondary: '#666' },
  },
  typography: {
    fontFamily: "'Roboto', 'Arial', sans-serif",
    h2: { fontSize: { xs: '20px', sm: '24px', md: '28px' }, fontWeight: 700 },
    h4: { fontSize: { xs: '24px', sm: '28px', md: '32px' }, fontWeight: 700, color: '#121212' },
    h5: { fontSize: { xs: '20px', sm: '24px', md: '28px' }, fontWeight: 700, color: 'white' },
    h6: { fontSize: { xs: '18px', sm: '20px' }, fontWeight: 700, color: '#121212' },
    body1: { fontSize: { xs: '14px', sm: '16px' }, color: '#666' },
    body2: { fontSize: { xs: '12px', sm: '14px' }, color: '#555' },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '50px',
          padding: '8px 24px',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(90deg, #1450f0 0%, #ff6b35 100%)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
          transition: 'all 0.3s ease-in-out',
        },
      },
    },
  },
});

// Componente reutilizável para Cards
const CustomCard = ({ emoji, title, description, typeCard, topBorderColor }) => (
  <Card
    sx={{
      bgcolor: 'background.paper',
      p: { xs: 2, sm: 3 },
      minHeight: { xs: 180, sm: 220 },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      borderRadius: '16px',
      borderTop: typeCard && topBorderColor ? `6px solid ${topBorderColor}` : 'none',
      mt: typeCard ? 2 : 0,
    }}
  >
    <CardContent sx={{ flexGrow: 1, p: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
      {!typeCard && (
        <Box
          sx={{
            bgcolor: '#ffe0b2',
            borderRadius: '50%',
            width: { xs: 60, sm: 80 },
            height: { xs: 60, sm: 80 },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mb: 2,
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          }}
        >
          {typeof emoji === 'string' ? (
            <Typography variant="h4" sx={{ fontSize: { xs: '30px', sm: '40px' }, lineHeight: 1 }}>
              {emoji}
            </Typography>
          ) : (
            React.cloneElement(emoji, { sx: { fontSize: { xs: '30px', sm: '40px' }, color: theme.palette.primary.main } })
          )}
        </Box>
      )}
      <Typography
        variant="h6"
        component="h3"
        sx={{ mb: 1, color: 'text.primary', fontSize: { xs: '16px', sm: '20px' }, fontWeight: 700 }}
      >
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '12px', sm: '14px' } }}>
        {description}
      </Typography>
    </CardContent>
  </Card>
);

// Modal de Call to Action
const CallToActionModal = ({ open, handleClose }) => (
  <Modal open={open} onClose={handleClose} aria-labelledby="call-to-action-modal-title">
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '90%', sm: 400 },
        bgcolor: 'background.paper',
        borderRadius: 12,
        boxShadow: 24,
        p: { xs: 2, sm: 4 },
        textAlign: 'center',
      }}
    >
      <IconButton
        aria-label="fechar"
        onClick={handleClose}
        sx={{ position: 'absolute', right: 8, top: 8, color: 'grey.500' }}
      >
        <CloseIcon />
      </IconButton>
      <Typography id="call-to-action-modal-title" variant="h5" sx={{ mb: 2, color: 'primary.main' }}>
        Fale Conosco!
      </Typography>
      <Typography sx={{ mb: 2, color: 'text.secondary' }}>
        Escolha a melhor forma de entrar em contato:
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Button
          variant="contained"
          startIcon={<PhoneIcon />}
          href="tel:+555138961707"
          fullWidth
          size="medium"
        >
          Ligar
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<WhatsAppIcon />}
          href="https://wa.me/5551999660542"
          target="_blank"
          fullWidth
          size="medium"
        >
          WhatsApp
        </Button>
        <Button
          variant="outlined"
          startIcon={<LocationOnIcon />}
          href="https://www.google.com/maps/search/Rua+Jos%C3%A9+da+Mala+Martins,+97+Porto+Alegre,+RS+91260-470"
          target="_blank"
          fullWidth
          size="medium"
        >
          Ver no Mapa
        </Button>
      </Box>
    </Box>
  </Modal>
);

// Componente para Scroll Top
const ScrollTop = ({ children, window }) => {
  const trigger = useScrollTrigger({
    target: window ? window() : undefined,
    disableHysteresis: true,
    threshold: 100,
  });

  const handleClick = (event) => {
    const anchor = (event.target.ownerDocument || document).querySelector('#back-to-top-anchor');
    if (anchor) {
      anchor.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  };

  return (
    <Slide appear={false} direction="up" in={trigger}>
      <Box onClick={handleClick} role="presentation" sx={{ position: 'fixed', bottom: { xs: 16, sm: 24 }, right: { xs: 8, sm: 16 } }}>
        {children}
      </Box>
    </Slide>
  );
};

// Componente principal
function App(props) {
  const [openCallToActionModal, setOpenCallToActionModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpenCallToActionModal = () => setOpenCallToActionModal(true);
  const handleCloseCallToActionModal = () => setOpenCallToActionModal(false);
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    handleMenuClose();
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ bgcolor: 'background.default' }}>
        {/* Modernized Main Header */}
        <Slide appear={false} direction="down" in={!useScrollTrigger()}>
  <AppBar position="fixed" sx={{ py: { xs: 1, sm: 1.5 } }}>
    <Toolbar component={Container} maxWidth="lg" disableGutters sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          transition: 'transform 0.3s ease',
          '&:hover': { transform: 'scale(1.05)' },
        }}
      >
        <Box
          component="img"
          src="/imagens/2.png"
          alt="Logo Multi Resíduos"
          sx={{
            // ALTERAÇÃO MANTIDA: Aumentada a altura do logo de 30px para 36px e ajustada a escala para xs
            height: { xs: 36, sm: 40 },
            transform: { xs: 'scale(2.8)', sm: 'scale(3.6)' },
            transformOrigin: 'left center',
            mr: 2,
          }}
        />
        <Typography
          variant="h6"
          sx={{
            color: 'white',
            fontWeight: 700,
            display: { xs: 'none', sm: 'block' },
          }}
        >
          Multi Resíduos
        </Typography>
      </Box>
      <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2 }}>
        <Button
          color="inherit"
          sx={{ color: 'white', fontWeight: 600, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
          onClick={() => scrollToSection('nossos-servicos')}
        >
          Nossos Serviços
        </Button>
        <Button
          color="inherit"
          sx={{ color: 'white', fontWeight: 600, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
          onClick={() => scrollToSection('tipos-de-residuos')}
        >
          Tipos de Resíduos
        </Button>
        <Button
          color="inherit"
          sx={{ color: 'white', fontWeight: 600, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
          onClick={() => scrollToSection('quem-somos')}
        >
          Quem Somos
        </Button>
        <Button
          color="inherit"
          sx={{ color: 'white', fontWeight: 600, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
          onClick={() => scrollToSection('contato')}
        >
          Contato
        </Button>
      </Box>
      {/* ALTERAÇÃO CORRIGIDA: Aproximar botão e MenuIcon sem inverter posição */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 2 } }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenCallToActionModal}
          sx={{
            fontWeight: 600,
            px: { xs: 2, sm: 3 },
            py: { xs: 0.5, sm: 1 },
            fontSize: { xs: '12px', sm: '14px' },
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            transition: 'transform 0.3s ease',
            color: '#fff', // Texto branco
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 16px rgba(0,0,0,0.4)',
            },
          }}
        >
          <span style={{ fontSize: { xs: '16px', sm: '18px' }, mr: 1 }}>📞</span> Peça Agora
        </Button>
        <IconButton
          sx={{ display: { xs: 'block', md: 'none' }, color: 'white' }}
          onClick={handleMenuOpen}
        >
          <MenuIcon />
        </IconButton>
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        sx={{ mt: 1 }}
      >
        <MenuItem onClick={() => scrollToSection('nossos-servicos')}>Nossos Serviços</MenuItem>
        <MenuItem onClick={() => scrollToSection('tipos-de-residuos')}>Tipos de Resíduos</MenuItem>
        <MenuItem onClick={() => scrollToSection('quem-somos')}>Quem Somos</MenuItem>
        <MenuItem onClick={() => scrollToSection('contato')}>Contato</MenuItem>
      </Menu>
    </Toolbar>
  </AppBar>
</Slide>
        <Toolbar id="back-to-top-anchor" />

       {/* Seção Banner */}
<Box
  component="section"
  sx={{
    py: { xs: 4, md: 12 },
    minHeight: { xs: '50vh', md: '70vh' },
    bgcolor: 'secondary.main',
    color: 'white',
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
    // ALTERAÇÃO 1: Usar Bannercelular.png em xs e banner.png em sm+
    backgroundImage: {
      xs: 'url(/imagens/Bannercelular.png)', // Imagem específica para celular
      sm: 'url(/imagens/banner.png)', // Imagem original para telas maiores
    },
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundBlendMode: 'overlay',
    '&:before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1,
    },
  }}
>
  <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
    <Typography
      variant="h1"
      component="h1"
      sx={{
        fontSize: { xs: '2rem', sm: '2.5rem', md: '4.5rem' },
        fontWeight: 800,
        mb: { xs: 2, md: 3 },
        letterSpacing: '-0.02em',
        lineHeight: 1.2,
        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
      }}
    >
      Aluguel de Caçambas com Agilidade e Confiança
    </Typography>
    <Typography
      variant="h5"
      component="p"
      sx={{
        maxWidth: { xs: '100%', md: '800px' },
        mx: 'auto',
        fontSize: { xs: '1rem', md: '1.5rem' },
        fontWeight: 300,
        lineHeight: 1.7,
        mb: { xs: 3, md: 5 },
        opacity: 0.9,
      }}
    >
      Soluções rápidas e sustentáveis para Porto Alegre e região. Gestão de resíduos e entulho para todos os projetos. Solicite um orçamento!
    </Typography>
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 1, md: 3 }, flexWrap: 'wrap' }}>
      <Button
        variant="contained"
        color="primary"
        size="large"
        onClick={handleOpenCallToActionModal}
        sx={{
          fontWeight: 700,
          px: { xs: 2, md: 5 },
          py: { xs: 1, md: 1.5 },
          fontSize: { xs: '0.9rem', md: '1.1rem' },
          borderRadius: '50px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          color: '#fff', // Texto branco
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.4)',
          },
        }}
      >
        Solicitar Caçamba
      </Button>
      <Button
        variant="outlined"
        color="inherit"
        size="large"
        href="#tipos-de-residuos"
        sx={{
          fontWeight: 600,
          px: { xs: 2, md: 5 },
          py: { xs: 1, md: 1.5 },
          fontSize: { xs: '0.9rem', md: '1.1rem' },
          borderRadius: '50px',
          borderColor: 'white',
          color: 'white',
          transition: 'transform 0.3s ease',
          '&:hover': {
            transform: 'translateY(-3px)',
            bgcolor: 'rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        Tipos de Resíduos
      </Button>
    </Box>
    <Box sx={{ mt: { xs: 2, md: 4 }, display: 'flex', justifyContent: 'center', gap: { xs: 1, md: 4 }, flexWrap: 'wrap' }}>
      <Typography variant="body2" sx={{ fontWeight: 400, opacity: 0.9, color: 'white', fontSize: { xs: '0.9rem', md: '1.1rem' } }}>
        🛠️ Atendimento 24/7 
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 400, opacity: 0.9, color: 'white', fontSize: { xs: '0.9rem', md: '1.1rem' } }}>
        🌱 Sustentabilidade Garantida
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 400, opacity: 0.9, color: 'white', fontSize: { xs: '0.9rem', md: '1.1rem' } }}>
        🚚 Entrega Rápida
      </Typography>
    </Box>
  </Container>
</Box>

        {/* Seção Nossos Serviços */}
        <Box
          component="section"
          id="nossos-servicos"
          sx={{
            py: { xs: 4, md: 8 },
            bgcolor: 'background.default',
            mt: 0,
            mb: 0,
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  fontWeight: 700,
                  color: 'primary.main',
                  fontSize: { xs: '20px', sm: '24px', md: '28px' },
                  mb: 2,
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    display: 'block',
                    width: 80,
                    height: 4,
                    bgcolor: 'primary.light',
                    mx: 'auto',
                    mt: 2,
                    borderRadius: '2px',
                  },
                }}
              >
                Nossos Serviços
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontSize: { xs: '14px', sm: '16px' }, color: 'text.secondary' }}
              >
                Soluções completas para gestão de resíduos
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: { xs: 2, md: 3 },
              }}
            >
              {[
                {
                  emoji: '🚚',
                  title: 'Entrega Rápida',
                  description: 'Entregamos sua caçamba no local e horário que você precisar, com total pontualidade.',
                },
                {
                  emoji: '💳',
                  title: 'Pagamento Flexível',
                  description: 'Aceitamos dinheiro, PIX, cartão de crédito e débito.',
                },
                {
                  emoji: '♻️',
                  title: 'Descarte Consciente',
                  description: 'Descarte adequado dos resíduos, todas as nossas caçambas possuem autorização da EPTC e Licença Ambiental.',
                },
              ].map((service, index) => (
                <Box key={index} sx={{ width: { xs: '100%', sm: '320px' } }}>
                  <CustomCard {...service} />
                </Box>
              ))}
            </Box>
          </Container>
        </Box>

        {/* Seção Por que Escolher */}
        <Box
          component="section"
          sx={{
            py: { xs: 4, md: 8 },
            bgcolor: '#1450f0',
            color: 'white',
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: { xs: 2, md: 4 } }}>
              <Typography
                variant="h5"
                gutterBottom
                sx={{ fontSize: { xs: '20px', sm: '24px', md: '28px' }, fontWeight: 700, color: 'white' }}
              >
                Por que escolher a Multi Resíduos?
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: { xs: 2, md: 2 },
                justifyContent: 'center',
                pb: { xs: 1, md: 2 },
                px: { xs: 0.5, md: 1 },
              }}
            >
              {[
                { emoji: '⏰', title: 'Atendimento 24h', description: 'Mediante agendamento. Sempre disponíveis para suas necessidades.' },
                { emoji: '📍', title: 'Porto Alegre e região', description: '(Alvorada, Canoas,Viamão e Porto Alegre).  Verifique se atendemos sua área ' },
                { emoji: '🛡️', title: 'Segurança', description: 'Equipamentos seguros e profissionais treinados para melhor atender.' },
                { emoji: '💰', title: 'Melhor Preço', description: 'Valores especiais para condomínios parceiros e empresas com demanda recorrente.                      .' },
              ].map((feature, index) => (
                <Box
                  key={index}
                  sx={{
                    flex: {
                      xs: '0 0 calc(50% - 16px)',
                      sm: '0 0 calc(50% - 16px)',
                      md: '0 0 calc(25% - 16px)',
                    },
                    maxWidth: {
                      xs: 'calc(50% - 16px)',
                      sm: 'calc(50% - 16px)',
                      md: 'calc(25% - 16px)',
                    },
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': { transform: 'scale(1.03)', boxShadow: 4 },
                  }}
                >
                  <CustomCard {...feature} />
                </Box>
              ))}
            </Box>
          </Container>
        </Box>
{/* Seção Tipos de Resíduos */}
{/* Seção Tipos de Resíduos */}
<Box
  component="section"
  id="tipos-de-residuos"
  sx={{
    py: { xs: 4, md: 8 },
    bgcolor: theme.palette.background.default,
  }}
>
  <Container maxWidth="lg">
    <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
       <Typography
                variant="h2"
                component="h2"
                sx={{
                  fontWeight: 700,
                  color: 'primary.main',
                  fontSize: { xs: '20px', sm: '24px', md: '28px' },
                  mb: 2,
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    display: 'block',
                    width: 80,
                    height: 4,
                    bgcolor: 'primary.light',
                    mx: 'auto',
                    mt: 2,
                    borderRadius: '2px',
                  },
                }}
              >
        Tipos de Resíduos
      </Typography>
      <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  maxWidth: { xs: '90%', sm: '70%', md: '600px' },
                  mx: 'auto',
                  fontSize: { xs: '14px', sm: '16px' },
                  lineHeight: 1.6,
                }}
              >
        Aceitamos diversos tipos de materiais, classificados conforme as normas ambientais. Escolha a caçamba certa para seu descarte!
      </Typography>
    </Box>
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: { xs: 2, md: 2 },
        justifyContent: 'center',
        pb: { xs: 1, md: 2 },
        px: { xs: 0.5, md: 1 },
        alignItems: 'stretch', // Garante que todos os cards tenham a mesma altura
      }}
    >
      {[
         {
          title: 'Classe A',
          description: 'Concreto, Tijolo, Pisos, Telhas, Solos, Cerâmicas e Rochas',
          topBorderColor: '#e53e3e',
          icon: '🧱',
          image: '/imagens/tijolos.jpg', // Imagem para Classe A
        },
        {
          title: 'Classe B',
          description: 'Plásticos, Madeiras, Papéis, Papelões, Metais, Vidros e Ferro',
          topBorderColor: '#38a169',
          icon: '♻️',
          image: '/imagens/tipob.jpg', // ALTERAÇÃO 1: Imagem específica para Classe B
        },
        {
          title: 'Classe C',
          description: 'Gesso',
          topBorderColor: '#3182ce',
          icon: '🧊',
          image: '/imagens/tipoc.jpg', // Imagem temporária para Classe C
        },
        {
          title: 'Classe D',
          description: 'Tintas, Vernizes, Pilhas, Baterias, Óleo, Cimento Amianto e Lâmpadas',
          topBorderColor: '#d69e2e',
          icon: '⚠️',
          image: '/imagens/tiipod.jpg', // Imagem temporária para Classe D
        },
      ].map((waste, index) => (
        <Box
          key={index}
          sx={{
            flex: {
              xs: '0 0 calc(50% - 16px)',
              sm: '0 0 calc(50% - 16px)',
              md: '0 0 calc(25% - 16px)',
            },
            maxWidth: {
              xs: 'calc(50% - 16px)',
              sm: 'calc(50% - 16px)',
              md: 'calc(25% - 16px)',
            },
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': { transform: 'scale(1.03)', boxShadow: 6 },
          }}
        >
          <Card
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2,
              bgcolor: 'white',
              boxShadow: 2,
              overflow: 'hidden', // Garante que a imagem não ultrapasse as bordas
              borderTop: `6px solid ${waste.topBorderColor}`, // Mantém a borda superior colorida
            }}
          >
            {/* ALTERAÇÃO 2: Remover máscara colorida da imagem */}
            <Box
              sx={{
                position: 'relative',
                height: { xs: 100, sm: 120, md: 140 }, // Altura da imagem
                backgroundImage: `url(${waste.image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                borderTopLeftRadius: 2,
                borderTopRightRadius: 2,
              }}
            />
            <CardContent
              sx={{
                flexGrow: 1,
                p: { xs: 1.5, md: 3 },
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                variant="h4"
                sx={{ fontSize: { xs: '20px', md: '24px' }, mb: 1 }}
              >
                {waste.icon}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  fontSize: { xs: '16px', sm: '20px' },
                  color: 'text.primary',
                }}
              >
                {waste.title}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: '12px', sm: '14px' } }}
              >
                {waste.description}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      ))}
    </Box>
  </Container>
</Box>

        {/* Seção Quem Somos */}
        <Box
          component="section"
          id="quem-somos"
          sx={{
            py: { xs: 5, md: 8 },
            bgcolor: 'background.default',
            overflow: 'hidden',
          }}
        >
          <Container maxWidth="lg">
            {/* Título da Seção */}
            <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  fontWeight: 700,
                  color: 'primary.main',
                  fontSize: { xs: '20px', sm: '24px', md: '28px' },
                  mb: 2,
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    display: 'block',
                    width: 80,
                    height: 4,
                    bgcolor: 'primary.light',
                    mx: 'auto',
                    mt: 2,
                    borderRadius: '2px',
                  },
                }}
              >
                Quem Somos
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  maxWidth: { xs: '90%', sm: '70%', md: '600px' },
                  mx: 'auto',
                  fontSize: { xs: '14px', sm: '16px' },
                  lineHeight: 1.6,
                }}
              >
                Conheça nossa história e o compromisso com a sustentabilidade e a gestão responsável de resíduos.
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: 4, md: 5 },
                alignItems: 'flex-start', // Manter o alinhamento no topo, permitindo que o texto defina a altura
              }}
            >
              {/* Texto na esquerda */}
              <Box
                sx={{
                  flex: 1, // Permite que o texto ocupe o espaço disponível
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                  p: { xs: 3, sm: 5 },
                  minHeight: { md: 400 }, // Opcional: defina uma altura mínima para o contêiner de texto
                  // Isso ajuda a garantir que ele tenha altura suficiente para as imagens
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between', // Garante que o botão fique no final
                }}
              >
                <Box> {/* Conteúdo de texto agrupado */}
                  <Typography variant="body1" sx={{ fontSize: '1rem', lineHeight: 1.7, color: 'text.secondary', mb: 2 }}>
                    Somos uma <strong>empresa prestadora de serviços</strong> comprometida com o meio ambiente. Atuamos na
                    <strong> destinação correta dos resíduo</strong> da construção civil, do comércio e também do pequeno gerador.
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: '1rem', lineHeight: 1.7, color: 'text.secondary', mb: 2 }}>
                    Com mais de <strong>15 anos de experiência</strong>, oferecemos <strong>locação de caçambas estacionárias</strong>,
                    sempre respeitando as <strong>normas legais</strong> e licenças necessárias.
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: '1rem', lineHeight: 1.7, color: 'text.secondary' }}>
                    Contamos com autorizações da <strong>EPTC</strong> para estacionamento das caçambas e
                     <strong> Licença de Operação da SMAM</strong> (L.O. 018068/2019), garantindo um serviço confiável e sustentável.
                  </Typography>
                </Box>

                <Box sx={{ textAlign: 'left', mt: 4 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleOpenCallToActionModal}
                    sx={{
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                      fontSize: '1rem',
                      borderRadius: '50px',
                      textTransform: 'none',
                      boxShadow: '0 4px 15px rgba(0, 123, 255, 0.25)',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: '0 6px 20px rgba(0, 123, 255, 0.35)',
                      },
                    }}
                  >
                    Solicite um Orçamento
                  </Button>
                </Box>
              </Box>

              {/* Imagens na direita com altura adaptada */}
              <Box
                sx={{
                  flex: 2, // Permite que as imagens ocupem o espaço disponível
                  maxWidth: { xs: '100%', md: '300px' },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                  // Removendo justifyContent: 'space-between' daqui para evitar que as imagens se estiquem
                  // em vez disso, vamos definir uma altura explícita para cada imagem.
                }}
              >
                <Box
                  component="img"
                  src="/imagens/Caçamba.jpg"
                  alt="Caçamba estacionária para coleta de resíduos"
                  sx={{
                    width: '100%',
                    height: 200, // Altura fixa para a primeira imagem
                    objectFit: 'cover',
                    borderRadius: 2,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/400x180?text=Caçamba';
                  }}
                />
                <Box
                  component="img"
                  src="/imagens/Caminhao.jpg"
                  alt="Caminhão da empresa realizando transporte eficiente"
                  sx={{
                    width: '100%',
                    height: 180, // Altura fixa para a segunda imagem
                    objectFit: 'cover',
                    borderRadius: 2,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/400x180?text=Caminhao';
                  }}
                />
              </Box>
            </Box>
          </Container>
        </Box>

        {/* Seção Contato */}
        <Box
          component="section"
          id="contato"
          sx={{
            py: { xs: 4, md: 8 },
            bgcolor: 'secondary.main',
            color: '#fff',
            mt: { xs: 4, md: 8 },
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#fff', fontSize: { xs: '24px', sm: '28px', md: '32px' } }}>
                Entre em Contato
              </Typography>
              <Typography sx={{ color: '#e0e0e0', mt: 1, fontSize: { xs: '14px', sm: '16px' } }}>
                Estamos prontos para atender você!
              </Typography>
            </Box>
            <Grid
              container
              spacing={{ xs: 2, md: 4 }}
              justifyContent="center"
              alignItems="center"
              sx={{ mb: { xs: 4, md: 6 } }}
            >
              <Grid item xs={6} sm={3} md={2}>
                <Box sx={{ textAlign: 'center' }}>
                  <PhoneIcon fontSize="large" sx={{ color: '#fff' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1, color: '#f5f5f5', fontSize: { xs: '14px', sm: '16px' } }}>
                    Telefone
                  </Typography>
                  <Typography sx={{ color: '#dcdcdc', fontSize: { xs: '12px', sm: '14px' } }}>(51) 3386-1707</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <Box sx={{ textAlign: 'center' }}>
                  <WhatsAppIcon fontSize="large" sx={{ color: '#25D366' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1, color: '#f5f5f5', fontSize: { xs: '14px', sm: '16px' } }}>
                    WhatsApp
                  </Typography>
                  <Typography sx={{ color: '#dcdcdc', fontSize: { xs: '12px', sm: '14px' } }}>(51) 99966-0542</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <Box sx={{ textAlign: 'center' }}>
                  <EmailIcon fontSize="large" sx={{ color: '#fff' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1, color: '#f5f5f5', fontSize: { xs: '14px', sm: '16px' } }}>
                    E-mail
                  </Typography>
                  <Typography sx={{ color: '#dcdcdc', fontSize: { xs: '12px', sm: '14px' } }}>multi.residuos@hotmail.com</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <LocationOnIcon fontSize="large" sx={{ color: '#fff' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1, color: '#f5f5f5', fontSize: { xs: '14px', sm: '16px' } }}>
                    Endereço
                  </Typography>
                  <Typography sx={{ color: '#dcdcdc', whiteSpace: 'pre-line', fontSize: { xs: '12px', sm: '14px' } }}>
                    Rua José da Maia Martins, 97{'\n'}Porto Alegre – RS{'\n'}91260-470
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#f5f5f5', fontSize: { xs: '14px', sm: '16px' } }}>
                    Siga-nos
                  </Typography>
                  <Box sx={{ display: 'inline-flex', gap: { xs: 1.5, md: 2 } }}>
  {/* ALTERAÇÃO 1: Aumentar tamanho do ícone e adicionar cor de destaque para Instagram */}
  <IconButton
    component="a"
    href="https://www.instagram.com/multiresiduospoa/"
    target="_blank"
    rel="noopener"
    sx={{
      color: '#fff',
      // Adicionar fundo gradiente inspirado no Instagram
      background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
      borderRadius: '50%',
      p: 1, // Pequeno padding para o fundo não encostar no ícone
      transition: 'transform 0.3s ease, opacity 0.3s ease',
      '&:hover': {
        transform: 'scale(1.1)', // Aumenta ligeiramente no hover
        opacity: 0.9, // Leve redução de opacidade no hover
      },
    }}
  >
    <InstagramIcon sx={{ fontSize: { xs: 32, md: 40 } }} /> {/* ALTERAÇÃO 2: Tamanho maior do ícone */}
  </IconButton>
  {/* ALTERAÇÃO 3: Aumentar tamanho do ícone e adicionar cor de destaque para Facebook */}
  <IconButton
    component="a"
    href="https://www.facebook.com/multi.residuospoa/"
    target="_blank"
    rel="noopener"
    sx={{
      color: '#fff',
      backgroundColor: '#1877F2', // Cor oficial do Facebook
      borderRadius: '50%',
      p: 1, // Pequeno padding para o fundo não encostar no ícone
      transition: 'transform 0.3s ease, opacity 0.3s ease',
      '&:hover': {
        transform: 'scale(1.1)', // Aumenta ligeiramente no hover
        opacity: 0.9, // Leve redução de opacidade no hover
      },
    }}
  >
    <FacebookIcon sx={{ fontSize: { xs: 32, md: 40 } }} /> {/* ALTERAÇÃO 4: Tamanho maior do ícone */}
  </IconButton>
</Box>
                </Box>
              </Grid>
            </Grid>
            <Box
              sx={{
                width: '100%',
                height: { xs: 200, sm: 250, md: 450 },
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: 3,
                border: '2px solid #fff',
              }}
            >
              <iframe
                title="Localização Multi Resíduos Porto Alegre"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3457.1790!2d-51.11619!3d-30.02371!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x95197775f0c365e5%3A0x9a2f4e6f21d18c91!2sR.%20Jos%C3%A9%20da%20Maia%20Martins%2C%2097%20-%20Porto%20Alegre%20-%20RS!5e0!3m2!1spt-BR!2sbr!4v1718012345678"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </Box>
          </Container>
        </Box>

        <Fab
          color="success"
          href="https://wa.me/5551999660542"
          target="_blank"
          sx={{ position: 'fixed', bottom: { xs: 64, md: 80 }, right: { xs: 8, sm: 16 } }}
          aria-label="whatsapp"
        >
          <WhatsAppIcon />
        </Fab>
        <ScrollTop {...props}>
          <Fab color="primary" size="small" aria-label="scroll back to top">
            <KeyboardArrowUpIcon />
          </Fab>
        </ScrollTop>
        <CallToActionModal open={openCallToActionModal} handleClose={handleCloseCallToActionModal} />
      </Box>
    </ThemeProvider>
  );
}

export default App;
